import { Link } from "react-router-dom";
import { Phone, MessageSquare, Mail, MapPin } from "lucide-react";
import { PHONE, TEL, SMS, EMAIL, MAILTO, LOCATION, NAV } from "@/lib/config";
import { useContent } from "@/lib/ContentContext";
import { resolveImage } from "@/lib/api";
import { APP_VERSION } from "@/lib/pwa";
import { InstallButton } from "./PwaBits";
import { SocialLinks } from "./SocialLinks";

export const Footer = () => {
  const { content } = useContent();
  const creditText = content.footer_credit || "Website Built & Managed by Prestigious Companies";
  const showCredit = content.footer_credit_show !== false;
  const creditUrl = content.footer_credit_url;
  const logoSrc = content?.site_logo ? resolveImage(content.site_logo) : "/brand/logo.png";

  return (
    <footer className="bg-[var(--navy)] text-white" data-testid="site-footer">
      <div className="max-w-7xl mx-auto px-4 py-14 grid grid-cols-1 md:grid-cols-4 gap-10">
        <div className="md:col-span-1">
          <img src={logoSrc} alt="Veterans Power Wash & Cleaning Services"
               className="h-20 w-auto max-w-[160px] rounded-xl mb-4 object-contain" />
          <p className="font-head font-bold text-xl uppercase tracking-tight">Veterans Power Wash</p>
          <p className="text-sm text-white/70 mb-4">& Cleaning Services</p>
          <div className="flex flex-wrap gap-2 text-[11px]">
            <span className="bg-[var(--red)] px-2.5 py-1 rounded-full font-semibold">Veteran-Owned</span>
            <span className="bg-white/10 px-2.5 py-1 rounded-full">Licensed & Insured</span>
            <span className="bg-white/10 px-2.5 py-1 rounded-full">Residential & Commercial</span>
          </div>
        </div>

        <div>
          <h4 className="overline text-[var(--aqua)] mb-4">Quick Links</h4>
          <ul className="space-y-2 text-sm text-white/80">
            {NAV.slice(0, 6).map((n) => (
              <li key={n.to}><Link to={n.to} className="hover:text-[var(--aqua)]">{n.label}</Link></li>
            ))}
            <li><Link to="/get-a-quote" className="hover:text-[var(--aqua)]">Get a Free Quote</Link></li>
          </ul>
        </div>

        <div>
          <h4 className="overline text-[var(--aqua)] mb-4">Contact</h4>
          <ul className="space-y-3 text-sm text-white/80">
            <li><a href={TEL} className="flex items-center gap-2 hover:text-[var(--aqua)]"><Phone className="w-4 h-4" /> {PHONE}</a></li>
            <li><a href={SMS} className="flex items-center gap-2 hover:text-[var(--aqua)]"><MessageSquare className="w-4 h-4" /> Text Us</a></li>
            <li><a href={MAILTO} className="flex items-center gap-2 hover:text-[var(--aqua)] break-all"><Mail className="w-4 h-4" /> {EMAIL}</a></li>
            <li className="flex items-center gap-2"><MapPin className="w-4 h-4" /> {LOCATION}</li>
          </ul>
        </div>

        <div>
          <h4 className="overline text-[var(--aqua)] mb-4">Company</h4>
          <ul className="space-y-2 text-sm text-white/80">
            <li><Link to="/services" className="hover:text-[var(--aqua)]">Our Services</Link></li>
            <li><Link to="/before-after" className="hover:text-[var(--aqua)]">Work Gallery</Link></li>
            <li><Link to="/privacy" className="hover:text-[var(--aqua)]">Privacy Policy</Link></li>
            <li><Link to="/terms" className="hover:text-[var(--aqua)]">Terms of Use</Link></li>
            <li><Link to="/accessibility" className="hover:text-[var(--aqua)]">Accessibility</Link></li>
          </ul>
          <p className="text-[11px] text-white/50 mt-4 leading-relaxed">
            By providing your phone number you consent to receive calls and texts about your request. Message and data rates may apply. Reply STOP to opt out.
          </p>
          <SocialLinks className="mt-6" />
          <div className="mt-4"><InstallButton variant="dark" /></div>
        </div>
      </div>

      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 py-5 flex flex-col sm:flex-row items-center justify-between gap-2 text-xs text-white/60">
          <p>© {new Date().getFullYear()} Veterans Power Wash & Cleaning Services. All rights reserved. <span className="text-white/30" data-testid="footer-version">Version {APP_VERSION}</span></p>
          {showCredit && (
            <p data-testid="footer-credit">
              {creditUrl ? (
                <>Website Built & Managed by <a href={creditUrl} target="_blank" rel="noopener noreferrer" className="text-white/80 hover:text-[var(--aqua)] underline">Prestigious Companies</a></>
              ) : creditText}
            </p>
          )}
        </div>
      </div>
    </footer>
  );
};
